/**
 * Visor de los documentos legales dentro de la app (pantalla C del diseño
 * aprobado el 25-sep-2026).
 *
 * Se abre desde cualquier enlace legal sin salir de la pantalla: al cerrar,
 * el usuario vuelve exactamente a donde estaba, con sus casillas intactas.
 * Cierra con Escape, con el botón o tocando fuera.
 */
import { useEffect, useRef, useState } from "react";

import { LegalDocumentBody } from "@/components/legal/LegalDocumentBody";
import { LEGAL_DOCUMENTS, LEGAL_DOC_IDS, type LegalDocId } from "@/legal/documentos";

export function LegalViewer({
  initialDoc,
  onClose,
}: {
  initialDoc: LegalDocId;
  onClose: () => void;
}) {
  const [active, setActive] = useState<LegalDocId>(initialDoc);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const doc = LEGAL_DOCUMENTS[active];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const switchTo = (id: LegalDocId) => {
    setActive(id);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  return (
    <div
      data-testid="legal-viewer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-viewer-title"
      className="fixed inset-0 z-100 flex items-start justify-center overflow-y-auto bg-slate-900/45 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl sm:max-h-[calc(100vh-4rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <h2 id="legal-viewer-title" className="text-base font-bold text-slate-900">
              Documentos legales
            </h2>
            <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-bold text-brand-700">
              Versión {doc.version}
            </span>
          </div>
          <div role="tablist" aria-label="Documento" className="flex gap-1">
            {LEGAL_DOC_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active === id}
                data-testid={`legal-viewer-tab-${id}`}
                onClick={() => switchTo(id)}
                className={
                  "rounded-md px-3 py-1.5 text-sm font-semibold " +
                  (active === id
                    ? "bg-brand-50 text-brand-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900")
                }
              >
                {LEGAL_DOCUMENTS[id].shortTitle}
              </button>
            ))}
          </div>
        </div>

        <div ref={bodyRef} role="tabpanel" className="overflow-y-auto px-5 py-4 sm:px-6">
          <h3 className="mb-4 text-lg font-extrabold tracking-tight text-slate-900">{doc.title}</h3>
          <LegalDocumentBody doc={doc} idPrefix="visor-" />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-500">Vigente desde el {doc.vigenteDesde}</span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            data-testid="legal-viewer-close"
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
